/**
 * ApexDrawer — fixed right-side push-aside panel hosting ApexChat + HistoryRail.
 *
 * Implemented as a plain <aside> rather than Antd <Drawer> so:
 *  - We never paint a backdrop mask over the rest of the page (Apex is a
 *    co-pilot, not a takeover).
 *  - The page can read `apexWidth` from useApex() and shift its right margin
 *    to make room (push-aside layout) on wide viewports. On narrow viewports
 *    the panel still floats over content because pushing would crowd it out.
 *  - We control a left-edge resize handle directly without fighting Antd's
 *    drawer animation.
 *
 * Keyboard support on the resize handle: Left/Right arrows nudge by 16px,
 * Shift+Arrow nudges by 64px. ESC closes the panel.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import { Button, Space, Typography } from "antd";
import {
  CloseOutlined,
  HistoryOutlined,
  PlusOutlined,
  RobotOutlined,
} from "@ant-design/icons";

import { APP_BRAND_NAME } from "../../lib/brand";
import { ditColors } from "../../lib/theme";
import {
  APEX_WIDTH_MAX,
  APEX_WIDTH_MIN,
  useApex,
} from "./ApexProvider";
import ApexChat from "./ApexChat";
import HistoryRail from "./HistoryRail";

function clamp(n: number, lo: number, hi: number): number {
  if (n < lo) return lo;
  if (n > hi) return hi;
  return n;
}

export default function ApexDrawer() {
  const {
    drawerOpen,
    closeDrawer,
    historyOpen,
    toggleHistory,
    apexWidth,
    setApexWidth,
    newChat,
  } = useApex();

  const handleRef = useRef<HTMLDivElement | null>(null);

  // ESC closes the panel — preserves the parity we had with Antd Drawer.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeDrawer();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen, closeDrawer]);

  // Pointer drag: capture and translate movement into a new width measured
  // from the right edge of the viewport.
  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const target = e.currentTarget;
      const pointerId = e.pointerId;
      try {
        target.setPointerCapture(pointerId);
      } catch {
        /* jsdom / non-supporting browsers */
      }

      const onMove = (ev: PointerEvent) => {
        const next = clamp(
          window.innerWidth - ev.clientX,
          APEX_WIDTH_MIN,
          APEX_WIDTH_MAX
        );
        setApexWidth(next);
      };
      const onUp = () => {
        try {
          target.releasePointerCapture(pointerId);
        } catch {
          /* ignore */
        }
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [setApexWidth]
  );

  // Keyboard accessibility on the resize handle.
  const onHandleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const step = e.shiftKey ? 64 : 16;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        // Wider: handle moves left (mouse x decreases).
        setApexWidth(apexWidth + step);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        // Narrower.
        setApexWidth(apexWidth - step);
      } else if (e.key === "Home") {
        e.preventDefault();
        setApexWidth(APEX_WIDTH_MIN);
      } else if (e.key === "End") {
        e.preventDefault();
        setApexWidth(APEX_WIDTH_MAX);
      }
    },
    [apexWidth, setApexWidth]
  );

  const panelStyle = useMemo<React.CSSProperties>(
    () => ({
      position: "fixed",
      top: 0,
      right: 0,
      height: "100vh",
      width: apexWidth,
      display: "flex",
      flexDirection: "column",
      background: ditColors.white,
      boxShadow: "-4px 0 16px rgba(9, 41, 64, 0.16)",
      zIndex: 1050,
      // Keep the inner content above the resize handle so clicks go through.
      isolation: "isolate",
    }),
    [apexWidth]
  );

  if (!drawerOpen) return null;

  return (
    <aside
      data-testid="apex-drawer"
      role="complementary"
      aria-label={`Ask ${APP_BRAND_NAME}`}
      style={panelStyle}
    >
      {/* Left-edge resize handle */}
      <div
        ref={handleRef}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize Apex panel"
        aria-valuemin={APEX_WIDTH_MIN}
        aria-valuemax={APEX_WIDTH_MAX}
        aria-valuenow={apexWidth}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onKeyDown={onHandleKeyDown}
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: 8,
          cursor: "col-resize",
          // Visually thin but with a hairline of brand color for affordance.
          background: "transparent",
          borderLeft: `1px solid ${ditColors.secondaryLight}`,
          zIndex: 2,
          touchAction: "none",
        }}
        // Subtle focus ring for keyboard users — DIT secondary on white.
        onFocus={(e) => {
          e.currentTarget.style.outline = `2px solid ${ditColors.secondaryOnWhite}`;
          e.currentTarget.style.outlineOffset = "-2px";
        }}
        onBlur={(e) => {
          e.currentTarget.style.outline = "";
          e.currentTarget.style.outlineOffset = "";
        }}
      />

      {/* DIT-themed header */}
      <div
        style={{
          background: ditColors.primary,
          color: ditColors.white,
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${ditColors.primaryHover}`,
          // Sit above the resize handle so clicks on title / buttons aren't
          // captured by it.
          position: "relative",
          zIndex: 1,
        }}
      >
        <Space size={10}>
          <RobotOutlined style={{ color: ditColors.white }} aria-hidden />
          <Typography.Text strong style={{ color: ditColors.white, fontSize: 16 }}>
            Ask {APP_BRAND_NAME}
          </Typography.Text>
        </Space>
        <Space size={6}>
          <Button
            size="small"
            type="text"
            icon={<PlusOutlined aria-hidden />}
            aria-label="New chat"
            onClick={newChat}
            style={{ color: ditColors.white }}
          >
            New chat
          </Button>
          <Button
            size="small"
            type="text"
            icon={<HistoryOutlined aria-hidden />}
            aria-label={historyOpen ? "Hide chat history" : "Show chat history"}
            aria-pressed={historyOpen}
            onClick={toggleHistory}
            style={{ color: ditColors.white }}
          >
            History
          </Button>
          <Button
            size="small"
            type="text"
            icon={<CloseOutlined aria-hidden />}
            aria-label="Close Apex"
            onClick={closeDrawer}
            style={{ color: ditColors.white }}
          />
        </Space>
      </div>

      {/* Body: optional left rail + main chat pane */}
      <div
        style={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          position: "relative",
          zIndex: 1,
        }}
      >
        {historyOpen ? <HistoryRail /> : null}
        <div style={{ flex: 1, padding: 16, minWidth: 0, display: "flex" }}>
          <ApexChat />
        </div>
      </div>
    </aside>
  );
}
